import type { Dictionary } from './types';

/**
 * Español.
 *
 * Written as Spanish, not as a Portuguese sentence with different vowels. The places
 * where that matters are the ones a word-by-word pass gets wrong: `licencia` is a
 * different legal concept from a Portuguese `licença`, and a Latin American reader
 * parses the whole rights line differently from a Portuguese one.
 *
 * `CNPJ` is kept exactly as the registry writes it. Translating an official company
 * identifier is how a legal notice stops matching the record it is supposed to cite.
 */
export const es: Dictionary = {
  meta: {
    title: 'Orobas: OrobasHipercube y Orobas Work para macOS',
    description:
      'OrobasHipercube y Orobas Work: dos aplicaciones nativas para macOS, escritas en SwiftUI. Un agente de IA super poderoso que actúa sobre tu proyecto real y una IDE nativa. En beta. Una cuenta, una licencia de por vida. Producto de M2ON.',
    ogTitle: 'Orobas: un agente de IA super poderoso y una IDE nativa.',
    ogDescription:
      'OrobasHipercube es un agente de IA super poderoso que actúa sobre tu proyecto real. Orobas Work es la IDE nativa donde ocurre el trabajo. SwiftUI nativo, macOS 26, en beta.',
  },

  chrome: {
    skipToContent: 'Saltar al contenido',
    brandName: 'Orobas',
    navMain: 'Principal',
    navApps: 'Aplicaciones',
    navPlans: 'Planes',
    navSkins: 'Skins',
    signIn: 'Entrar',
    myAccount: 'Mi cuenta',
    openMenu: 'Abrir menú',
    closeMenu: 'Cerrar menú',
    languageLabel: 'Idioma',
    platformBadge: 'para macOS',
    downloadInstaller: 'Descargar instalador',
  },

  footer: {
    blurb:
      'Dos aplicaciones nativas para macOS, escritas en SwiftUI. Sin Electron, sin WebKit, sin telemetría. Producto de M2ON.',
    paymentsLine: 'Pagos mediante Mercado Pago.',
    rights: '© {year} {company} - {legalId}.',
    reserved: 'TODOS LOS DERECHOS RESERVADOS.',
    companyName: 'M2ON LTDA',
    legalId: 'CNPJ: 68.657.000/0001-00',
    companyLinkLabel: 'M2ON, el sitio de la empresa (se abre en una pestaña nueva)',
  },

  home: {
    heroShortcut: 'abre {app} desde cualquier lugar',
    heroTitleLine1: 'Dos aplicaciones nativas.',
    heroTitleLine2: 'Un solo agente.',
    heroBody:
      '*{agent}* es un agente de IA super poderoso que actúa sobre tu proyecto real. *{work}* es la IDE nativa donde ocurre el trabajo. En beta, con foco en desarrolladores de software, con el objetivo de poder usarse en cualquier tipo de trabajo en macOS. Escritas en SwiftUI, producto de M2ON.',
    ctaSignIn: 'Entrar',
    ctaAccount: 'Mi cuenta',
    ctaMeetApps: 'Conoce las aplicaciones',
    plateAlt: 'Ventana compacta de {app}',
    wallpaperAlt:
      'Papel pintado de Orobas: el escudo en trazo blanco sobre un fondo oscuro.',
    opensApp: 'abre {app}',
    ownedBadge: 'Licencia activa en esta cuenta',
    notOwnedBadge: 'Todavía sin licencia en esta cuenta',
    togetherTitle: 'Una cuenta, una licencia, tres lugares',
    togetherBody:
      'Entras con el mismo correo en el sitio y en las dos aplicaciones. La licencia es una fila en la base de datos, no un archivo en tu equipo, así que vale en los tres sitios a la vez. Comprar en el sitio desbloquea las dos aplicaciones.',
    togetherColumns: [
      {
        title: 'En el sitio',
        body: 'La cuenta muestra qué está licenciado y dónde descargar cada instalador. Aquí es donde ocurre la compra.',
      },
      {
        title: 'Entre las dos aplicaciones',
        body: 'La sesión vive en un solo archivo, y cualquier cambio se anuncia entre los procesos. Entrar en una entra en la otra, incluido el cierre de sesión.',
      },
      {
        title: 'El control de acceso',
        body: 'Cada aplicación solo se abre si hay una sesión válida y la licencia de esa aplicación. La decisión se toma en la base de datos, igual en las dos.',
      },
    ],
    requirementsLine: 'Requisitos: {requirements}',
    installerTitle: 'Dos apps, un instalador',
    installerBody:
      'El Instalador Orobas descarga la versión más reciente de cada app, la verifica y la deja en /Applications. Una descarga en lugar de dos. La primera vez, haz clic derecho en la app y elige Abrir.',
    installerDownload: 'Descargar el instalador',
    installerVersion: 'Versión {version}',
  },

  pricing: {
    title: 'Una licencia de por vida por aplicación',
    body:
      'Pagas una vez y la usas para siempre, sin suscripción y sin límite de uso. Las aplicaciones están en beta. Con Ollama en tu equipo el coste de tokens es cero, así que la licencia es lo único que has pagado.',
    loading: 'Cargando planes',
    retry: 'Reintentar',
    emptyBody:
      'Ahora mismo no hay ningún plan a la venta. Escribe a {email} y te avisamos cuando abra.',
    footerNote:
      'Pago mediante Mercado Pago. La licencia se activa en cuanto se confirma el pago.',
    onceLabel: 'pago único, de por vida',
    bundleBadge: 'Pack',
    unavailable: 'No disponible',
    alreadyOwned: 'Ya es tuyo',
    signInToBuy: 'Entra para comprar',
    buy: 'Comprar',
    openingPayment: 'Abriendo el pago…',
    catalogueFailed: 'No se pudieron cargar los planes.',
    checkoutFailed: 'No se pudo iniciar el pago.',
    teaserTitle: 'Una cuenta. Las dos aplicaciones.',
    teaserBody:
      'Compra una vez y entra con la misma cuenta en el sitio y en las dos aplicaciones de macOS. La licencia es la misma en los tres sitios.',
    teaserCta: 'Ver planes',
  },

  account: {
    crestAlt: 'Escudo de Orobas',
    loadingAccount: 'Cargando tu cuenta',
    signInTitle: 'Entra en tu cuenta',
    signUpTitle: 'Crea tu cuenta',
    signInBody:
      'La misma cuenta sirve para el sitio, para OrobasHipercube y para Orobas Work.',
    signUpBody:
      'Una cuenta para las dos aplicaciones y para el sitio. La licencia se lee desde cualquiera de los dos lados.',
    emailLabel: 'Correo electrónico',
    passwordLabel: 'Contraseña',
    signingIn: 'Entrando…',
    signingUp: 'Creando la cuenta…',
    signInAction: 'Entrar',
    signUpAction: 'Crear cuenta',
    signInWithGoogle: 'Entrar con Google',
    signInWithGitHub: 'Entrar con GitHub',
    orDivider: 'o',
    noAccountYet: '¿Todavía no tienes cuenta?',
    hasAccount: '¿Ya tienes cuenta?',
    createOne: 'Crear una',
    confirmTitle: 'Confirma tu correo',
    confirmBody:
      'La cuenta se creó para *{email}*. Abre el enlace que llegó a tu bandeja de entrada para confirmarla. Sin eso no se puede entrar.',
    checking: 'Comprobando…',
    confirmedSignIn: 'Ya la confirmé, entrar',
    wrongPassword: 'La contraseña no coincide.',
    backToSignIn: 'Volver a entrar',
    accountLabel: 'Cuenta',
    yourAccount: 'Tu cuenta',
    signOut: 'Salir',
    licensesTitle: 'Tus licencias',
    refreshing: 'Actualizando…',
    refresh: 'Actualizar',
    noLicensesBody:
      'Todavía no hay ninguna aplicación licenciada en esta cuenta. En cuanto se confirme un pago, la licencia aparecerá aquí y en las dos aplicaciones al mismo tiempo.',
    viewPlans: 'Ver planes',
    lifetimeLicense: 'Licencia de por vida',
    licensedFallback: 'Licenciado',
    validUntil: 'hasta el {date}',
    installersTitle: 'Instaladores',
    download: 'Descargar',
    versionLabel: 'Versión {version}',
    gatekeeperNote:
      'Los dos paquetes están firmados ad-hoc y todavía no han pasado por la notarización de Apple. La primera vez, haz clic derecho en la aplicación dentro del zip y elige Abrir. Gatekeeper avisa una sola vez.',
    oauthFailedTitle: 'No se pudo completar el inicio de sesión',
    oauthFailedBody: 'Inténtalo de nuevo, o entra con tu correo y contraseña.',
    order: {
      readFailedTitle: 'No se pudo leer el pedido',
      signInRequiredTitle: 'Entra para ver este pedido',
      signInRequiredBody:
        'Esta página muestra el resultado de un pedido hecho con la cuenta que compró.',
      checkingTitle: 'Comprobando tu pago…',
      checkingBody: 'Suele tardar unos segundos.',
      approvedTitle: 'Pago confirmado',
      approvedBody:
        'Tu licencia está activa. Ya vale para OrobasHipercube, para Orobas Work y para esta cuenta.',
      incompleteTitle: 'El pago no se completó',
      incompleteBody: 'No se cobró nada y no se activó ninguna licencia.',
      expiredTitle: 'Este pedido expiró',
      expiredBody: 'El medio de pago caducó antes de la confirmación.',
      pendingTitle: 'Esperando la confirmación',
      pendingBody:
        'Mercado Pago todavía está confirmando el pago. Esta página se actualiza sola.',
      goToAccount: 'Ir a mi conta',
    },
  },

  skinNFT: {
    title: 'Skins de OrobasHipercube',
    subtitle: 'Personaliza tu agente con accesorios exclusivos',
    owned: 'Skins que posees',
    available: 'Skins disponibles',
    equip: 'Equipar',
    unequip: 'Desequipar',
    purchase: 'Comprar',
    loading: 'Cargando skins…',
    emptyOwned: 'Aún no tienes ninguna skin. ¡Compra en la tienda para personalizar tu OrobasHipercube!',
    emptyAvailable: 'No hay skins disponibles en este momento.',
    slotHat: 'Sombrero',
    slotGlasses: 'Gafas',
    slotEye: 'Ojos',
    slotNose: 'Nariz',
    slotMouth: 'Boca',
    slotBody: 'Cuerpo',
    rarityCommon: 'Común',
    rarityRare: 'Raro',
    rarityEpic: 'Épico',
    rarityLegendary: 'Legendario',
    equippedBadge: 'Equipado',
    syncMessage: 'Tus skins se sincronizan automáticamente con la app OrobasHipercube en macOS.',
    previewTitle: 'Vista previa',
    animationNote: 'Los accesorios de ojos, nariz y boca siguen la animación de tu agente.',
    editionLabel: 'Edición {number}',
    editionShort: '#{number}',
  },

  callback: {
    crestAlt: 'Escudo de Orobas',
    openingTitle: 'Abriendo {app}…',
    openingBody: 'Un momento.',
    blockedTitle: '{app} no se abrió automáticamente',
    blockedBody:
      'Algunos navegadores bloquean el paso a una aplicación. Pulsa abajo para abrir {app} y terminar el inicio de sesión.',
    openAppCta: 'Abrir {app}',
    declinedTitle: 'Inicio de sesión cancelado',
    declinedCancelled: 'Cancelaste el inicio de sesión con Google.',
    declinedGeneric: 'Google rechazó el inicio de sesión.',
    unknownTitle: 'Este enlace no es de una aplicación',
    unknownBody:
      'La dirección que abrió esta página no nombra ninguna aplicación de Orobas, así que no hay a dónde devolver el inicio de sesión.',
    incompleteTitle: 'Inicio de sesión incompleto',
    incompleteBody:
      'Faltó {missing} en esta vuelta. Abre la aplicación y empieza el inicio de sesión otra vez, tarda unos segundos y te trae de vuelta aquí.',
    signInHere: 'Entrar en el sitio',
  },

  api: {
    offline: 'Sin conexión con el servidor. Revisa tu internet e inténtalo de nuevo.',
    unexpected: 'Algo salió mal. Inténtalo de nuevo.',
    signInFailed: 'No se pudo entrar. Inténtalo de nuevo.',
    sessionExpired: 'La sesión expiró. Vuelve a entrar.',
    alreadyRegistered: 'Este correo ya tiene cuenta. Entra con tu contraseña.',
    accountOffline: 'Sin conexión con el servidor de cuentas.',
    badCredentials: 'Correo o contraseña incorrectos.',
    emailNotConfirmed:
      'Confirma tu correo antes de entrar. El enlace llegó a tu bandeja de entrada.',
    passwordTooShort: 'La contraseña necesita al menos 6 caracteres.',
    invalidEmail: 'Ese correo no parece válido.',
    rateLimited: 'Demasiados intentos. Espera un momento e inténtalo de nuevo.',
  },
};